import { createContext, useCallback, useContext, useState, useRef, type ReactNode } from "react";

interface PdfViewerPayload {
  /** Object URL or signed URL pointing to the document. */
  url: string;
  /** Display filename (used in the title bar and as the share/download name). */
  filename: string;
  /** Optional original Blob — enables native Web Share with the actual file. */
  blob?: Blob;
  /**
   * Qué se está enseñando. Nació siendo solo PDF, pero las facturas escaneadas
   * también son fotos: media docena de las que hay guardadas son `.jpg`, y sin
   * esto el visor las mandaba a PDF.js, que no puede leerlas — «no se pudo
   * mostrar el PDF» encima de una foto perfectamente visible.
   *
   * Sin decir nada se deduce de la extensión del nombre.
   */
  kind?: "pdf" | "image";
}

interface PdfViewerContextValue {
  isOpen: boolean;
  payload: PdfViewerPayload | null;
  open: (p: PdfViewerPayload) => void;
  close: () => void;
}

const PdfViewerContext = createContext<PdfViewerContextValue | null>(null);

export function PdfViewerProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<PdfViewerPayload | null>(null);
  // Track the currently-open object URL so we can revoke it on close.
  const lastObjectUrlRef = useRef<string | null>(null);

  const open = useCallback((p: PdfViewerPayload) => {
    // Revoke any previously-shown blob URL before opening a new doc.
    if (lastObjectUrlRef.current && lastObjectUrlRef.current !== p.url) {
      try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch { /* ignore */ }
    }
    if (p.url.startsWith("blob:")) {
      lastObjectUrlRef.current = p.url;
    } else {
      lastObjectUrlRef.current = null;
    }
    setPayload(p);
  }, []);

  const close = useCallback(() => {
    setPayload(null);
    if (lastObjectUrlRef.current) {
      const url = lastObjectUrlRef.current;
      lastObjectUrlRef.current = null;
      // Defer revocation so the iframe has time to unload first.
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }, 1000);
    }
  }, []);

  return (
    <PdfViewerContext.Provider value={{ isOpen: payload !== null, payload, open, close }}>
      {children}
    </PdfViewerContext.Provider>
  );
}

export function usePdfViewer(): PdfViewerContextValue {
  const ctx = useContext(PdfViewerContext);
  if (!ctx) {
    throw new Error("usePdfViewer must be used inside a <PdfViewerProvider>");
  }
  return ctx;
}

/**
 * Module-level singleton so non-React code (e.g. `deliverPdf`) can open the
 * viewer without needing the context. The provider registers itself once
 * mounted; deliverPdf calls `getPdfViewerHandle()?.open(...)`.
 */
let viewerHandle: { open: (p: PdfViewerPayload) => void; close: () => void } | null = null;

export function registerPdfViewerHandle(handle: typeof viewerHandle) {
  viewerHandle = handle;
}

export function getPdfViewerHandle() {
  return viewerHandle;
}
